import ActingBanner from '../components/ActingBanner'
import ErrorBoundary from '../components/ErrorBoundary'
import { NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { useSkin } from '../lib/skin'
import { useIsMobile } from '../lib/useMediaQuery'
import Wordmark from '../components/Wordmark'
import Icon from '../components/Icon'
import type { IconName } from '../components/Icon'
import TabBar from '../components/mobile/TabBar'
import { useEntitlements } from '../lib/entitlements'
import type { FeatureKey } from '../lib/plans'

/**
 * The app frame.
 *
 * Sidebar on a desktop, bottom tab bar on a phone — the same shape the iOS app
 * uses, because a 240pt sidebar on a 390pt screen leaves no app. Ticketing is
 * present but disabled on desktop, matching `FeatureFlags.ticketingEnabled`;
 * it's dropped from the phone bar entirely, since five is the ceiling for a
 * bottom nav and a permanently disabled sixth would be the one to cut.
 */
interface NavEntry {
  to: string
  label: string
  icon: IconName
  end?: boolean
  soon?: boolean
  /** Present in the phone tab bar. */
  mobile?: boolean
  /** Hidden unless the resolved plan permits this feature. */
  feature?: FeatureKey
}

const NAV: NavEntry[] = [
  { to: '/', label: 'Dashboard', icon: 'dashboard', end: true, mobile: true },
  { to: '/tours', label: 'Tours', icon: 'tours', mobile: true },
  // Shown only to plans that sell a roster. A solo user should never be
  // offered a page whose entire purpose is something they haven't bought —
  // and their one auto-created artist is meant to stay invisible.
  { to: '/artists', label: 'Artists', icon: 'mic', feature: 'artist_profiles' },
  { to: '/database', label: 'Database', icon: 'database', mobile: true },
  { to: '/export', label: 'Export', icon: 'export' },
  { to: '/account', label: 'My Account', icon: 'account', mobile: true },
]

export default function Shell() {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <div style={{ minHeight: '100dvh', background: 'var(--bg)' }}>
        <a className="skip" href="#main">
          Skip to content
        </a>
        {/* No inner scroller on a phone. The document scrolls, which is the
            only thing that lets Safari collapse its URL bar — a nested
            `overflow: auto` pins the chrome open and clips the page under it.
            Each mobile screen supplies its own nav bar. */}
        <ActingBanner />
        <main id="main">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
        <TabBar />
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'var(--sidebar-w) minmax(0, 1fr)',
        height: '100%',
        background: 'var(--bg)',
      }}
    >
      <a className="skip" href="#main">
        Skip to content
      </a>
      <Sidebar />
      <main id="main" style={{ overflow: 'auto', minWidth: 0 }}>
        <ActingBanner />
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  )
}

/* ---------------------------------------------------------------- desktop */

function Sidebar() {
  const { user, signOut } = useAuth()
  const { permits } = useEntitlements()

  return (
    <aside
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 26,
        padding: '26px 0 18px',
        background: 'var(--panel)',
        borderRight: 'var(--hairline-width) solid var(--hairline)',
        minHeight: 0,
      }}
    >
      <div className="row" style={{ padding: '0 22px', gap: 8 }}>
        <Wordmark height={19} />
      </div>

      <nav className="stack" style={{ gap: 2 }}>
        {NAV.filter((entry) => !entry.feature || permits(entry.feature)).map((entry) => (
          <SidebarRow key={entry.to} {...entry} />
        ))}
      </nav>

      <div style={{ flex: 1 }} />

      <div className="stack" style={{ padding: '0 18px', gap: 14 }}>
        <SkinPicker />
        <div className="spread" style={{ gap: 8 }}>
          <span className="t-caption truncate" title={user?.email ?? undefined}>
            {user?.displayName || user?.email}
          </span>
          <button className="btn btn-quiet" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </div>
    </aside>
  )
}

function SidebarRow({ to, label, icon, end, soon }: NavEntry) {
  if (soon) {
    return (
      <span
        title="Ticketing is coming soon. Sell tickets, track sales and get paid out — all inside Encore."
        style={{ ...rowStyle(false), opacity: 0.4, cursor: 'not-allowed' }}
      >
        <Icon name={icon} />
        <span className="grow">{label}</span>
        <span className="chip">Soon</span>
      </span>
    )
  }

  return (
    <NavLink to={to} end={end} style={({ isActive }) => rowStyle(isActive)}>
      {({ isActive }) => (
        <>
          <Icon
            name={icon}
            strokeWidth={isActive ? 2 : 1.6}
            style={{ color: isActive ? 'var(--accent-text)' : 'inherit' }}
          />
          <span className="grow">{label}</span>
        </>
      )}
    </NavLink>
  )
}

function rowStyle(isActive: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 22px',
    fontSize: '0.9rem',
    fontWeight: isActive ? 600 : 400,
    color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
    background: isActive ? 'var(--panel-raised)' : 'transparent',
    boxShadow: isActive ? 'inset var(--signal-rule) 0 0 0 var(--accent)' : 'none',
  }
}

/* ----------------------------------------------------------------- mobile */



/* ------------------------------------------------------------- appearance */

/**
 * Three icons, no words: follow the system, Daylight, Showtime. Each carries an
 * accessible name and `aria-pressed` so it isn't icon-only to a screen reader.
 */
function SkinPicker({ compact = false }: { compact?: boolean }) {
  const { preference, setPreference } = useSkin()
  const options: Array<{
    value: 'system' | 'daylight' | 'showtime'
    icon: IconName
    label: string
  }> = [
    { value: 'system', icon: 'auto', label: 'Match system appearance' },
    { value: 'daylight', icon: 'sun', label: 'Daylight' },
    { value: 'showtime', icon: 'moon', label: 'Showtime' },
  ]

  return (
    <div
      role="group"
      aria-label="Appearance"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 2,
        padding: 3,
        background: 'var(--panel-raised)',
        borderRadius: 'var(--radius-control)',
      }}
    >
      {options.map((option) => {
        const selected = preference === option.value
        return (
          <button
            key={option.value}
            onClick={() => setPreference(option.value)}
            aria-label={option.label}
            aria-pressed={selected}
            title={option.label}
            style={{
              display: 'grid',
              placeItems: 'center',
              height: compact ? 30 : 34,
              width: compact ? 38 : undefined,
              borderRadius: 7,
              background: selected ? 'var(--panel)' : 'transparent',
              color: selected ? 'var(--text-primary)' : 'var(--text-tertiary)',
              boxShadow: selected ? 'var(--shadow-card)' : 'none',
            }}
          >
            <Icon name={option.icon} size={16} />
          </button>
        )
      })}
    </div>
  )
}
